import { z } from "zod";
import { siteLocaleSchema, siteLanguagesSchema, validateLanguageSelection } from "./site-languages";
import { googleMapsEmbedSchema } from "./maps";
import { legalNoticesSchema } from "./legal-notices";
import { fontFamilies, motionEffects, surfaceEffects } from "./design-options";
import { SEO_TITLE_MAX_LENGTH, SEO_DESCRIPTION_MAX_LENGTH } from "./seo-fields";
import { MAX_SITE_PAGES, MAX_CUSTOM_MENU_LINKS, MAX_HEADER_MENU_ITEMS, menuIconNames } from "./navigation-options";

const id = z.string().min(1).max(80);
const url = z.string().max(2048).optional();
const colour = z.string().regex(/^#[0-9a-fA-F]{6}$/);
const safeLink = z.string().max(500).refine(value => !value || /^(?:https?:\/\/[^\s]+|mailto:[^\s]+|tel:[+0-9() .-]+|\/(?!\/)[^\s]*|#[^\s]*)$/i.test(value), "Use https://, /page, #section, mailto: or tel: links");
const videoUrl = z.string().max(2048).refine(value => /^(?:https?:\/\/[^\s]+|\/(?!\/)[^\s]*)$/i.test(value), "Use an https:// video URL or /path");
const appearanceFields = {
  headingColor: colour.nullable().optional(), linkColor: colour.nullable().optional(),
  buttonBackground: colour.nullable().optional(), buttonTextColor: colour.nullable().optional(), buttonBorderColor: colour.nullable().optional(),
  buttonHoverBackground: colour.nullable().optional(), buttonHoverTextColor: colour.nullable().optional(),
  secondaryButtonBackground: colour.nullable().optional(), secondaryButtonTextColor: colour.nullable().optional(), secondaryButtonBorderColor: colour.nullable().optional(),
  secondaryButtonStyle: z.enum(["link", "solid", "outline", "glass"]).optional(),
  headingWeight: z.number().int().min(400).max(800).optional(), bodyWeight: z.number().int().min(400).max(700).optional(),
  headingLetterSpacing: z.number().min(-2).max(4).optional(), bodyLineHeight: z.number().min(1.2).max(2.2).optional(),
  buttonFontSize: z.number().int().min(10).max(22).optional(), borderColor: colour.nullable().optional(), borderWidth: z.number().int().min(0).max(6).optional()
};
export const pageDesignSchema = z.object({
  ...appearanceFields,
  background: z.enum(["default", "surface", "secondary", "primary", "custom", "gradient"]).optional(),
  backgroundColor: colour.optional(),
  gradientColor: colour.optional(), gradientAngle: z.number().int().min(0).max(360).optional(),
  textColor: colour.nullable().optional(), mutedColor: colour.nullable().optional(), surfaceColor: colour.nullable().optional(), accentColor: colour.nullable().optional(),
  primaryColor: colour.nullable().optional(), secondaryColor: colour.nullable().optional(),
  headingFont: z.enum(["inherit", ...fontFamilies]).optional(), bodyFont: z.enum(["inherit", ...fontFamilies]).optional(),
  spacing: z.enum(["inherit", "compact", "comfortable", "spacious"]).optional(),
  effect: z.enum(["inherit", ...surfaceEffects]).optional(),
  radius: z.enum(["inherit", "sharp", "soft", "round"]).optional(),
  buttonStyle: z.enum(["inherit", "solid", "outline", "glass"]).optional(), buttonShape: z.enum(["inherit", "rectangle", "rounded", "pill"]).optional(),
  contentWidth: z.enum(["inherit", "narrow", "standard", "wide"]).optional(), textScale: z.enum(["inherit", "small", "medium", "large"]).optional(),
  motion: z.enum(["inherit", ...motionEffects]).optional(),
  imageShape: z.enum(["inherit", "rounded", "square", "circle", "arch"]).optional()
}).strict();
export const sectionDesignSchema = pageDesignSchema.extend({
  align: z.enum(["left", "center", "right"]).optional(),
  layout: z.enum(["default", "reverse", "stacked"]).optional(),
  columns: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]).optional(),
  imagePosition: z.enum(["center", "top", "bottom", "left", "right", "bottom right"]).optional(),
  divider: z.enum(["none", "wave", "slant", "curve"]).optional()
});
const baseSection = { id, visible: z.boolean(), design: sectionDesignSchema.optional() };

const hero = z.object({
  ...baseSection,
  type: z.literal("hero"),
  eyebrow: z.string().max(100),
  title: z.string().min(1).max(140),
  subtitle: z.string().max(500),
  presentation: z.enum(["cover", "split", "glass"]).optional(),
  backgroundVideo: z.object({ url: videoUrl, mobileUrl: videoUrl.optional(), poster: videoUrl.optional(), mobilePoster: videoUrl.optional(), showControls: z.boolean().optional() }).optional(),
  primaryLabel: z.string().max(80),
  primaryHref: safeLink,
  secondaryLabel: z.string().max(80).optional(),
  secondaryHref: safeLink.optional(),
  image: url,
  align: z.enum(["left", "center"])
});

const about = z.object({
  ...baseSection,
  type: z.literal("about"),
  eyebrow: z.string().max(100),
  title: z.string().max(160),
  body: z.string().max(4000),
  image: url,
  stats: z.array(z.object({ value: z.string().max(30), label: z.string().max(80) })).max(4).optional(),
  team: z.boolean().optional(),
  imageKind: z.enum(["photo", "illustration"]).optional()
});

const services = z.object({
  ...baseSection,
  type: z.literal("services"),
  eyebrow: z.string().max(100),
  title: z.string().max(160),
  intro: z.string().max(500),
  items: z.array(z.object({ id, title: z.string().max(120), description: z.string().max(500), icon: z.string().max(40), href: safeLink.optional() })).max(12)
});

const gallery = z.object({
  ...baseSection,
  type: z.literal("gallery"),
  layout: z.enum(["grid", "masonry", "cards"]).optional(),
  eyebrow: z.string().max(100),
  title: z.string().max(160),
  images: z.array(z.object({ id, url: z.string().max(2048), alt: z.string().max(200), href: safeLink.optional() })).max(24),
  showCaptions: z.boolean().optional()
});

const faq = z.object({ ...baseSection, type: z.literal("faq"), eyebrow: z.string().max(100), title: z.string().max(160), intro: z.string().max(500), items: z.array(z.object({ id, question: z.string().max(200), answer: z.string().max(1500) })).max(20) });

const testimonials = z.object({
  ...baseSection,
  type: z.literal("testimonials"),
  layout: z.enum(["grid", "carousel"]).optional(),
  replaceGoogleWidget: z.boolean().optional(),
  provider: z.literal("google").optional(),
  googleMapsUrl: safeLink.optional(),
  eyebrow: z.string().max(100),
  title: z.string().max(160),
  items: z.array(z.object({ id, quote: z.string().max(800), name: z.string().max(100), role: z.string().max(100).optional(), sourceUrl: safeLink.optional(), rating: z.number().int().min(1).max(5).optional() })).max(12)
});

const contact = z.object({
  ...baseSection,
  type: z.literal("contact"),
  eyebrow: z.string().max(100),
  title: z.string().max(160),
  intro: z.string().max(500),
  phone: z.string().max(50).optional(),
  email: z.string().email().or(z.literal("")).optional(),
  address: z.string().max(240).optional(),
  hours: z.string().max(240).optional(),
  showForm: z.boolean(),
  showSocials: z.boolean().optional(),
  detailsOrder: z.array(z.enum(["phone", "email", "address", "hours"])).max(4).refine(keys => new Set(keys).size === keys.length, "Contact details must not be repeated").optional(),
  mapsUrl: z.string().max(2048).refine(value => !value || /^(?:https?:\/\/[^\s]+|mailto:[^\s]+|tel:[+0-9() .-]+|\/(?!\/)[^\s]*|#[^\s]*)$/i.test(value), "Use a safe map link").optional(),
  showMap: z.boolean().optional(),
  mapEmbedUrl: googleMapsEmbedSchema.optional(),
  formButtonLabel: z.string().max(80).optional()
});

const cta = z.object({
  ...baseSection,
  type: z.literal("cta"),
  title: z.string().max(160),
  body: z.string().max(500),
  label: z.string().max(80),
  href: safeLink
});

export const sectionSchema = z.discriminatedUnion("type", [hero, about, services, gallery, testimonials, contact, cta, faq]);

export const themeSchema = z.object({
  ...appearanceFields,
  primary: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  secondary: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  accent: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  background: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  surface: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  text: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  muted: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  headingFont: z.enum(fontFamilies),
  bodyFont: z.enum(fontFamilies),
  radius: z.enum(["sharp", "soft", "round"]),
  buttonStyle: z.enum(["solid", "outline", "glass"]),
  buttonShape: z.enum(["rectangle", "rounded", "pill"]).optional(),
  surfaceEffect: z.enum(surfaceEffects).optional(),
  imageShape: z.enum(["rounded", "square", "circle", "arch"]).optional(),
  contentWidth: z.enum(["narrow", "standard", "wide"]).optional(),
  spacing: z.enum(["compact", "comfortable", "spacious"]).optional(),
  textScale: z.enum(["small", "medium", "large"]).optional(),
  motion: z.enum(motionEffects).optional()
});

export const siteObjectSchema = z.object({
  legalNotices: legalNoticesSchema.optional(),
  id,
  ownerId: id,
  name: z.string().min(1).max(100),
  slug: z.string().regex(/^[a-z0-9](?:[a-z0-9-]{1,46}[a-z0-9])?$/),
  status: z.enum(["draft", "published", "archived"]),
  locale: siteLocaleSchema,
  languages: siteLanguagesSchema.optional(),
  defaultLocale: siteLocaleSchema.optional(),
  localizations: z.record(siteLocaleSchema, z.object({ entries: z.array(z.object({ key: z.string().min(1).max(600), source: z.string().max(4000), text: z.string().max(4000) }).strict()).max(2000).refine(entries => new Set(entries.map(entry => entry.key)).size === entries.length, "Translation keys must be unique") }).strict()).optional(),
  theme: themeSchema,
  logo: url,
  favicon: url,
  navigation: z.array(z.object({ id, label: z.string().max(80), href: safeLink, parentId: z.string().max(100).nullable().optional(), icon: z.enum(menuIconNames).optional() }).strict()).max(MAX_CUSTOM_MENU_LINKS)
    .refine(links => new Set(links.map(link => link.id)).size === links.length, "Menu link IDs must be unique"),
  header: z.object({
    backgroundColor: colour.nullable().optional(), textColor: colour.nullable().optional(), linkColor: colour.nullable().optional(), buttonBackground: colour.nullable().optional(), buttonTextColor: colour.nullable().optional(),
    layout: z.enum(["left", "center"]).optional(),
    buttonBorderColor: colour.nullable().optional(),
    showBusinessName: z.boolean().optional(),
    mobileBusinessName: z.string().max(100).optional(),
    contactFontSize: z.number().int().min(11).max(22).optional(),
    sticky: z.boolean().optional(),
    effect: z.enum(["solid", "glass"]).optional(),
    showCta: z.boolean().optional(),
    ctaLabel: z.string().max(80).optional(),
    ctaHref: safeLink.optional(),
    showContact: z.boolean().optional(),
    linkOrder: z.array(z.string().min(1).max(100)).max(MAX_HEADER_MENU_ITEMS).optional()
  }).optional(),
  footer: z.object({
    backgroundColor: colour.nullable().optional(), textColor: colour.nullable().optional(), linkColor: colour.nullable().optional(),
    appearance: z.enum(["dark", "light"]).optional(),
    layout: z.enum(["columns", "stacked"]).optional(),
    description: z.string().max(500).optional(),
    showContact: z.boolean().optional(),
    showBranding: z.boolean().optional(),
    linkOrder: z.array(z.string().min(1).max(100)).max(MAX_SITE_PAGES + 3).optional()
  }).optional(),
  pages: z.array(z.object({
    id,
    template: z.enum(["blank", "services", "products", "about", "team", "process", "work", "gallery", "faq", "contact", "custom"]).optional(),
    slug: z.string().max(100).regex(/^(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)?$/, "Page paths may use lowercase letters, numbers and hyphens only"),
    title: z.string().min(1).max(100),
    seoTitle: z.string().max(SEO_TITLE_MAX_LENGTH),
    seoDescription: z.string().max(SEO_DESCRIPTION_MAX_LENGTH),
    showInNavigation: z.boolean(),
    showInFooter: z.boolean().optional(),
    noIndex: z.boolean().optional(),
    navigationLabel: z.string().max(80).optional(),
    navigationParentId: z.string().max(100).nullable().optional(),
    navigationIcon: z.enum(menuIconNames).optional(),
    footerLabel: z.string().max(80).optional(),
    design: pageDesignSchema.optional(),
    sections: z.array(sectionSchema).max(30).refine(sections => new Set(sections.map(section => section.id)).size === sections.length, "Section IDs must be unique on each page")
  })).min(1).max(MAX_SITE_PAGES)
    .refine(pages => pages[0]?.slug === "", "The first page must be the home page")
    .refine(pages => new Set(pages.map(page => page.slug)).size === pages.length, "Every page must have a unique path")
    .refine(pages => new Set(pages.map(page => page.id)).size === pages.length, "Page IDs must be unique"),
  social: z.object({
    facebook: url,
    instagram: url,
    linkedin: url,
    tiktok: url,
    youtube: url
  }),
  publishedVersion: z.number().int().nonnegative().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  publishedAt: z.string().optional(),
  aiSetupCompleted: z.boolean().optional(),
  pageStructureVersion: z.number().int().min(1).max(1).optional()
});

export const siteSchema = siteObjectSchema.superRefine(validateLanguageSelection);

export const siteUpdateSchema = siteObjectSchema.omit({ id: true, ownerId: true, createdAt: true }).partial().strict();

export const domainRequestSchema = z.object({
  siteId: id,
  hostname: z.string().trim().toLowerCase().regex(/^(?=.{4,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/)
});
